/**
 * ArchLang v0.10 — cross-package governance: `use policy <Name> from <pkg>`
 * evaluation (design §4; plan S23, engine half).
 *
 * `use policy <Name> from <pkg>` **instantiates** an exported policy at the use
 * site. The imported policy is evaluated *as if declared in the importing scope*:
 *
 *  - **Local scope, importer's data.** It runs over the IMPORTER's model
 *    (universe / graph / closure of the file that wrote the `use policy`), never
 *    the home package's — a home policy is a rule, the importer's model is what
 *    it bites.
 *  - **Lexical query closure.** Query names (and `violating <P>` refs) the policy
 *    mentions resolve in its HOME package (`ctx.queries` / `ctx.policies` are the
 *    home vocabulary), but those queries too evaluate over the importer's data.
 *    Vocabulary travels; data stays local ({@link EvalContext._queryStack}).
 *  - **Importer owns the instance.** The optional instance body
 *    (`use policy … { except …; escalate … }`) is the ONLY place the importer's
 *    waivers/escalations live. `except` folds onto the policy as an extra waiver;
 *    `escalate` raises severity at the use site (raise-only — a lowering escalate
 *    is a no-op, so it can never weaken the home-declared severity).
 *  - **Version pinned via `dependencies`; never remote-evaluated.** Resolution
 *    keys off the importer's already-resolved dependency {@link PackageMap} — the
 *    same in-process map `use … from …` resolves against. A package not in that
 *    map is a targeted diagnostic; nothing is ever fetched or run remotely.
 */

import type { Body, Diagnostic, ParsedFile } from "./types.js";
import type { PolicyDecl, QueryDecl, UsePolicyStmt, ExceptRule, EscalateRule } from "./v010-ast.js";
import type { EvalContext } from "./selector-eval.js";
import type { PackageMap } from "./package-resolver.js";
import { evalPolicy, activeFindings, findingDiagnostics, maxSeverity, type Finding, type Severity } from "./policy-eval.js";
import { makeDiag } from "./diagnostics/catalog.js";

export const USE_POLICY_PACKAGE_NOT_FOUND = "USE_POLICY_PACKAGE_NOT_FOUND";
export const USE_POLICY_NOT_FOUND = "USE_POLICY_NOT_FOUND";
export const USE_POLICY_NOT_EXPORTED = "USE_POLICY_NOT_EXPORTED";

/**
 * A package's governance surface. `exportedPolicies` is its **published**
 * governance (the `use policy` target set); `policies` + `queries` are its FULL
 * declaration set — the lexical closure an imported policy resolves against
 * (a home policy may reference a non-exported home query or a `violating`
 * sibling policy).
 */
export interface PackageGovernance {
  /** `export policy` declarations by name — importable governance. */
  readonly exportedPolicies: ReadonlyMap<string, PolicyDecl>;
  /** Every policy declared in the package (the home lexical scope). */
  readonly policies: ReadonlyMap<string, PolicyDecl>;
  /** Every query declared in the package (the home lexical query closure). */
  readonly queries: ReadonlyMap<string, QueryDecl>;
}

/**
 * Collect the `policy` / `query` declarations from a package's parsed files
 * into its {@link PackageGovernance}. Covers file-root (= space-level)
 * declarations AND module-body ones (v0.10 §5.2 — the heads join
 * `module_member`); only file-root policies are exportable, so the module-body
 * walk feeds the lexical closure, never the `use policy` target set.
 */
export function collectGovernance(files: readonly ParsedFile[]): PackageGovernance {
  const exportedPolicies = new Map<string, PolicyDecl>();
  const policies = new Map<string, PolicyDecl>();
  const queries = new Map<string, QueryDecl>();
  const walkBody = (body: Body): void => {
    for (const p of body.policies ?? []) {
      if (!policies.has(p.name)) policies.set(p.name, p);
    }
    for (const q of body.queries ?? []) {
      if (!queries.has(q.name)) queries.set(q.name, q);
    }
    for (const sub of body.modules) walkBody(sub.body);
  };
  for (const f of files) {
    for (const d of f.declarations) {
      if (d.kind === "policy") {
        // First declaration of a name wins (duplicates are a resolver concern).
        if (!policies.has(d.decl.name)) policies.set(d.decl.name, d.decl);
        if (d.decl.exported && !exportedPolicies.has(d.decl.name)) exportedPolicies.set(d.decl.name, d.decl);
      } else if (d.kind === "query") {
        if (!queries.has(d.decl.name)) queries.set(d.decl.name, d.decl);
      } else if (d.kind === "module") {
        walkBody(d.decl.body);
      }
    }
  }
  return { exportedPolicies, policies, queries };
}

/** The outcome of evaluating one `use policy` statement at its use site. */
export interface ImportedPolicyResult {
  /** True when the package + its `export policy` both resolved. */
  readonly resolved: boolean;
  /**
   * Findings of the imported policy over the IMPORTER's model — waivers (home +
   * instance body) and instance-body escalate already applied. Empty when
   * resolution failed. `activeFindings` filters to the gate-failing set.
   */
  readonly findings: Finding[];
  /** Resolution diagnostics (unknown package / policy / not-exported). */
  readonly diagnostics: Diagnostic[];
}

/**
 * Evaluate a `use policy <Name> from <pkg>` statement at the use site.
 *
 * `deps` is the importer's resolved dependency map (the same map `use … from …`
 * resolves against) — resolution keys off it, so a package that is not a
 * declared dependency is {@link USE_POLICY_PACKAGE_NOT_FOUND} and nothing is
 * fetched. `importerCtx` is an {@link EvalContext} over the IMPORTER's model;
 * this function overrides only its policy/query vocabulary with the home
 * package's (the lexical closure). `now` is the optional gate clock threaded to
 * waiver `until:` expiry (timeless when omitted).
 */
export function evalUsePolicy(
  stmt: UsePolicyStmt,
  deps: PackageMap,
  importerCtx: EvalContext,
  now?: Date,
): ImportedPolicyResult {
  const diagnostics: Diagnostic[] = [];
  const pkgName = stmt.fromPackage.join(".");

  // 1. Resolve the home package via the declared dependencies (never remote).
  const home = deps.get(pkgName);
  if (!home) {
    diagnostics.push(
      makeDiag("error", stmt.fromSource ?? stmt.source, USE_POLICY_PACKAGE_NOT_FOUND, "usePolicy.packageNotFound", {
        pkgName,
        name: stmt.name,
      }),
    );
    return { resolved: false, findings: [], diagnostics };
  }

  // 2. Resolve the exported policy in the home package.
  const gov = collectGovernance(home.input.files);
  const policy = gov.exportedPolicies.get(stmt.name);
  if (!policy) {
    diagnostics.push(
      gov.policies.has(stmt.name)
        ? makeDiag("error", stmt.nameSource ?? stmt.source, USE_POLICY_NOT_EXPORTED, "usePolicy.notExported", {
            name: stmt.name,
            pkgName,
          })
        : makeDiag("error", stmt.nameSource ?? stmt.source, USE_POLICY_NOT_FOUND, "usePolicy.notFound", {
            name: stmt.name,
            pkgName,
          }),
    );
    return { resolved: false, findings: [], diagnostics };
  }

  // 3. Home lexical closure: the home package's policy + query vocabulary over
  //    the importer's data (universe / graph / closure carried by importerCtx).
  const homeCtx: EvalContext = { ...importerCtx, policies: gov.policies, queries: gov.queries, now };

  // 4. Instance-body waivers: fold the use-site `except`s onto the policy so the
  //    shared `applyWaivers` (inside evalPolicy) suppresses at the use site too —
  //    ownership of waivers transfers to the importer with instantiation.
  const instanceExcepts = stmt.instanceBody.filter((r): r is ExceptRule => r.kind === "except");
  const evaluated: PolicyDecl = instanceExcepts.length
    ? { ...policy, rules: [...policy.rules, ...instanceExcepts] }
    : policy;

  let findings = evalPolicy(evaluated, homeCtx, now);

  // 5. Instance-body escalate: raise-only. Escalations targeting THIS policy —
  //    from the home body (self-escalation travels) or the instance body (the
  //    importer hardening a soft baseline) — join to the highest severity.
  //    Monotonicity: `maxSeverity` never lowers, so an escalate below the
  //    home-declared severity is inert and the home severity stays the floor.
  const raise = escalationFor(policy, stmt);
  if (raise) findings = findings.map((f) => ({ ...f, severity: maxSeverity(f.severity, raise) }));

  return { resolved: true, findings, diagnostics };
}

/**
 * The severity an imported policy is escalated to at the use site (raise-only
 * join over every `escalate <thisPolicy> to …` in the home policy body AND the
 * instance body), or undefined when none target it.
 */
function escalationFor(policy: PolicyDecl, stmt: UsePolicyStmt): Severity | undefined {
  const escalates: EscalateRule[] = [
    ...policy.rules.filter((r): r is EscalateRule => r.kind === "escalate"),
    ...stmt.instanceBody.filter((r): r is EscalateRule => r.kind === "escalate"),
  ];
  let raise: Severity | undefined;
  for (const e of escalates) {
    if (e.target.join(".") !== policy.name) continue; // targets another policy → inert here
    raise = raise ? maxSeverity(raise, e.severity) : e.severity;
  }
  return raise;
}

/**
 * Evaluate every `use policy` statement in an importer and fold the imported
 * policies' active violations into the diagnostic stream (alongside resolution
 * diagnostics). Timeless (no gate clock) — the shared engine→diagnostic surface,
 * mirroring {@link policyDiagnostics} for local policies. `advisory` findings are
 * report artifacts, not diagnostics.
 */
export function usePolicyDiagnostics(
  stmts: readonly UsePolicyStmt[],
  deps: PackageMap,
  importerCtx: EvalContext,
): Diagnostic[] {
  const out: Diagnostic[] = [];
  for (const stmt of stmts) {
    const r = evalUsePolicy(stmt, deps, importerCtx);
    out.push(...r.diagnostics);
    out.push(...findingDiagnostics(activeFindings(r.findings)));
  }
  return out;
}
